import {
  TransferReadingsValidationResponse,
  useTransferReadingsValidation,
} from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Alert, Button } from "react-bootstrap";
import AxpoInvoiceModal from "./AxpoInvoiceModal";

const formatKwh = (value: number): string =>
  `${value.toLocaleString("en-GB", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kWh`;

// Prices are in £/kWh; convert to p/kWh (×100), up to 3dp
const formatPricePerKwh = (priceKwh: number): string => {
  const pencePerKwh = priceKwh * 100;
  // Use up to 3dp, but strip trailing zeros
  const formatted = pencePerKwh.toFixed(3).replace(/\.?0+$/, "");
  return `${formatted}p`;
};

type StatusColor = "red" | "amber" | "green";

const STATUS_STYLES: Record<
  StatusColor,
  { icon: string; color: string; fontWeight: "bold" | "normal" }
> = {
  red: { icon: "\uD83D\uDD34", color: "#dc3545", fontWeight: "bold" },
  amber: { icon: "\uD83D\uDFE0", color: "#b8860b", fontWeight: "normal" },
  green: { icon: "\uD83D\uDFE2", color: "#1a7d2f", fontWeight: "normal" },
};

const getPeriodsColor = (pct: number): StatusColor => {
  if (pct < -5) return "red";
  if (pct < -1) return "amber";
  if (pct <= 1) return "green";
  if (pct <= 5) return "amber";
  return "red";
};

const formatPctSigned = (pct: number): string => {
  const rounded = Number(pct.toFixed(1));
  const sign = rounded >= 0 ? "+" : "";
  return `${sign}${rounded.toFixed(1)}%`;
};

const TransferReadingsValidation = ({
  siteId,
  esysUuid,
  month,
  isImport = false,
  counterpartyEsysUuid,
  currencyCode,
}: {
  siteId: string;
  esysUuid: string;
  month: string;
  isImport?: boolean;
  counterpartyEsysUuid: string;
  currencyCode: string;
}) => {
  const { fetchTransferReadingsValidation } = useTransferReadingsValidation({
    siteId,
    esysUuid,
    month,
    counterpartyEsysUuid,
    enabled: !!esysUuid && !!month,
  });
  const { data, isFetching, error } = fetchTransferReadingsValidation;

  if (isFetching) {
    return (
      <span
        style={{ fontWeight: "normal", fontStyle: "italic", color: "#666" }}
      >
        {" "}
        (validating...)
      </span>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="py-2 mb-0">
        Failed to load transfer readings validation
      </Alert>
    );
  }

  if (!data || !data.hasTransferReadings) {
    return (
      <div style={{ fontSize: "0.9em", color: "#666" }}>
        No transfer readings found for {month}
        {data && ` (InRange export: ${formatKwh(data.inrangeExportTotal)})`}
      </div>
    );
  }

  return (
    <TransferReadingsCompare
      data={data}
      isImport={isImport}
      currencyCode={currencyCode}
    />
  );
};

const TransferReadingsCompare = ({
  data,
  isImport,
  currencyCode,
}: {
  data: TransferReadingsValidationResponse;
  isImport: boolean;
  currencyCode: string;
}) => {
  const [showInvoice, setShowInvoice] = useState(false);
  const hasInvoice = data.invoiceLineItems && data.invoiceLineItems.length > 0;

  const isPartialMonth =
    data.monthInfo?.isFirstMonth && data.monthInfo?.isPartialMonth;

  const exportPct = data.exportPct ?? null;

  // Compute periods percentage vs InRange
  const periodsPct =
    data.inrangeExportPeriods &&
    data.inrangeExportPeriods > 0 &&
    data.matchedPeriods != null
      ? ((data.matchedPeriods - data.inrangeExportPeriods) /
          data.inrangeExportPeriods) *
        100
      : null;

  const renderStatusLine = (
    text: string,
    pct: number | null,
    colorFn: (pct: number) => StatusColor
  ) => {
    if (isPartialMonth || pct === null) {
      return <div style={{ color: "#666" }}>{text}</div>;
    }
    const status = colorFn(pct);
    const style = STATUS_STYLES[status];
    return (
      <div style={{ color: style.color, fontWeight: style.fontWeight }}>
        {style.icon} {text}
      </div>
    );
  };

  return (
    <div style={{ fontSize: "0.9em" }}>
      <div className="d-flex flex-column gap-1">
        {isPartialMonth && (
          <div style={{ color: "#666", fontStyle: "italic" }}>
            Partial month — site operated for {data.monthInfo!.operationalDays}{" "}
            day
            {data.monthInfo!.operationalDays !== 1 ? "s" : ""}
          </div>
        )}
        {(() => {
          const text = `Supplier metered ${isImport ? "import" : "export"}: ${data.axpoMeteredTotal != null ? formatKwh(data.axpoMeteredTotal) : "N/A"}${exportPct !== null ? ` (${formatPctSigned(exportPct)} vs InRange metered ${isImport ? "import" : "export"})` : ""}`;
          if (isPartialMonth || !data.exportRyg) {
            return <div style={{ color: "#666" }}>{text}</div>;
          }
          const style = STATUS_STYLES[data.exportRyg];
          return (
            <div style={{ color: style.color, fontWeight: style.fontWeight }}>
              {style.icon} {text}
            </div>
          );
        })()}
        {data.axpoSettledTotal != null && (
          <div style={{ color: "#666" }}>
            Supplier NBP volume: {formatKwh(data.axpoSettledTotal)}
          </div>
        )}
        {!isImport && data.matchedPeriods !== undefined && (
          <>
            {renderStatusLine(
              `Periods with non-zero export: ${data.matchedPeriods}${periodsPct !== null ? ` (${formatPctSigned(periodsPct)} vs InRange metered export)` : ""}`,
              periodsPct,
              getPeriodsColor
            )}
          </>
        )}
        {data.pricingStats && (
          <>
            {data.pricingStats.minPrice !== data.pricingStats.maxPrice && (
              <div style={{ color: "#666", marginTop: "4px" }}>
                Weighted average price:{" "}
                {formatPricePerKwh(data.pricingStats.avgPrice)} (min:{" "}
                {formatPricePerKwh(data.pricingStats.minPrice)}, max:{" "}
                {formatPricePerKwh(data.pricingStats.maxPrice)})
                {!isImport &&
                  data.modelledTariff != null &&
                  data.modelledTariff > 0 &&
                  (() => {
                    const pricePct =
                      ((data.pricingStats!.avgPrice - data.modelledTariff!) /
                        data.modelledTariff!) *
                      100;
                    return ` (${formatPctSigned(pricePct)} vs modelled tariff of ${formatPricePerKwh(data.modelledTariff!)})`;
                  })()}
              </div>
            )}
            {data.totalRevenue !== undefined && (
              <div style={{ color: "#666" }}>
                {isImport ? "Procurement cost" : "Total invoice amount"}: £
                {data.totalRevenue.toLocaleString("en-GB", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
                {!isImport && " (incl. VAT)"}
              </div>
            )}
          </>
        )}
        {data.negativePricingPeriods !== undefined &&
          data.negativePricingPeriods > 0 && (
            <>
              <div style={{ color: "#666", marginTop: "4px" }}>
                Periods with negative pricing: {data.negativePricingPeriods}
                {data.negativePricingStats &&
                  ` (min: ${formatPricePerKwh(data.negativePricingStats.minPrice)}, max: ${formatPricePerKwh(data.negativePricingStats.maxPrice)}, avg: ${formatPricePerKwh(data.negativePricingStats.avgPrice)})`}
              </div>
              {data.negativePricingVolume !== undefined && (
                <div style={{ color: "#666" }}>
                  Volume with negative pricing:{" "}
                  {formatKwh(data.negativePricingVolume)}
                  {data.axpoSettledTotal &&
                    data.axpoSettledTotal > 0 &&
                    ` (${((data.negativePricingVolume / data.axpoSettledTotal) * 100).toFixed(1)}% of total)`}
                </div>
              )}
              {data.negativePricingCost !== undefined && (
                <div style={{ color: "#666" }}>
                  Total cost of negative pricing: £
                  {Math.abs(data.negativePricingCost).toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              )}
            </>
          )}
        {hasInvoice && (
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowInvoice(true)}
            >
              View axpo invoice details
            </Button>
          </div>
        )}
      </div>
      {showInvoice && hasInvoice && (
        <AxpoInvoiceModal
          month={data.month}
          invoiceDate={data.invoiceDate ?? null}
          lineItems={data.invoiceLineItems!}
          currencyCode={currencyCode}
          vatRate={data.vatRate ?? 0.2}
          onClose={() => setShowInvoice(false)}
        />
      )}
    </div>
  );
};

export default TransferReadingsValidation;
